import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, Pencil, Zap, AlertCircle, DollarSign } from "lucide-react";
import { format, parseISO } from "date-fns";
import { useAuth } from "../lib/auth";

const FREQUENCIES = [
  { key: "monthly",     label: "Monthly",      months: 1 },
  { key: "quarterly",   label: "Quarterly",    months: 3 },
  { key: "biannually",  label: "Bi-annually",  months: 6 },
  { key: "annually",    label: "Annually",     months: 12 },
];
const FREQ_MONTHS = Object.fromEntries(FREQUENCIES.map((f) => [f.key, f.months]));
const PALETTE = ["#0066ff", "#7C3AED", "#10B981", "#F59E0B", "#EF4444", "#EC4899", "#06B6D4", "#71717A", "#84CC16", "#F97316"];

const money = (v) => typeof v === "number" ? `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—";

export default function Subscriptions() {
  const { user } = useAuth();
  // Subscriptions is now a read-only summary view — the source of truth is Passive Projects.
  const readOnly = true;
  const [sections, setSections] = useState([]);
  const [subs, setSubs] = useState([]);
  const [editSection, setEditSection] = useState(null); // section or {mode:"create"}
  const [editSub, setEditSub] = useState(null);

  const load = async () => {
    const [s, sub] = await Promise.all([api.get("/subscription-sections"), api.get("/subscriptions")]);
    setSections(s.data);
    setSubs(sub.data);
  };
  useEffect(() => { load(); }, []);

  const subsBySection = useMemo(() => {
    const m = {};
    sections.forEach((sec) => { m[sec.id] = []; });
    subs.forEach((s) => { if (m[s.section_id]) m[s.section_id].push(s); });
    return m;
  }, [sections, subs]);

  const totals = useMemo(() => {
    const out = { avgMonthly: 0, totalYear: 0 };
    subs.forEach((s) => {
      const amt = parseFloat(s.monthly_amount) || 0;
      const months = FREQ_MONTHS[s.frequency] || 1;
      // monthly_amount is treated as the recurring billed amount; convert to per-month
      const perMonth = amt / months;
      out.avgMonthly += perMonth;
      out.totalYear += perMonth * 12;
    });
    return out;
  }, [subs]);

  return (
    <div data-testid="subscriptions-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Recurring</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">Subscriptions</h1>
          <p className="mt-2 text-sm text-zinc-600">Track clients on recurring billing. Due dates auto-advance after each cycle.</p>
        </div>
        {!readOnly && (
          <button data-testid="subscriptions-add-section" onClick={() => setEditSection({ mode: "create" })} className="inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50">
            <Plus size={14} /> Add Section
          </button>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <SummaryCard label="Average Revenue / Month" value={money(totals.avgMonthly)} accent="text-emerald-700" testId="subs-avg-monthly" />
        <SummaryCard label="Total Revenue / Year" value={money(totals.totalYear)} accent="text-[#0066ff]" testId="subs-total-year" />
      </div>

      <div className="mt-6 flex flex-col gap-6">
        {sections.map((sec) => (
          <SectionCard
            key={sec.id}
            section={sec}
            subs={subsBySection[sec.id] || []}
            readOnly={readOnly}
            onAddSub={() => setEditSub({ mode: "create", section_id: sec.id })}
            onEditSub={(s) => setEditSub(s)}
            onEditSection={() => setEditSection(sec)}
            onReload={load}
          />
        ))}
        {sections.length === 0 && (
          <div className="rounded-xl border border-dashed border-zinc-200 bg-white p-10 text-center text-sm text-zinc-500">
            No sections yet. Add one to start tracking recurring billing.
          </div>
        )}
      </div>

      {editSection && (
        <SectionModal initial={editSection.mode === "create" ? null : editSection} onClose={() => setEditSection(null)} onSaved={() => { setEditSection(null); load(); }} />
      )}
      {editSub && (
        <SubscriptionModal
          initial={editSub.mode === "create" ? null : editSub}
          defaultSectionId={editSub.section_id || editSub?.section_id}
          sections={sections}
          onClose={() => setEditSub(null)}
          onSaved={() => { setEditSub(null); load(); }}
        />
      )}
    </div>
  );
}

function SectionCard({ section, subs, readOnly, onAddSub, onEditSub, onEditSection, onReload }) {
  const totals = useMemo(() => {
    let avg = 0, year = 0;
    subs.forEach((s) => {
      const amt = parseFloat(s.monthly_amount) || 0;
      const months = FREQ_MONTHS[s.frequency] || 1;
      const perMonth = amt / months;
      avg += perMonth;
      year += perMonth * 12;
    });
    return { avg, year };
  }, [subs]);

  const removeSub = async (id) => {
    if (!window.confirm("Delete this subscription?")) return;
    try {
      await api.delete(`/subscriptions/${id}`);
      toast.success("Deleted");
      onReload();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid={`subs-section-${section.id}`} className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
      <div className="flex items-center justify-between border-b border-zinc-100 px-5 py-4" style={{ borderTopColor: section.color, borderTopWidth: 4, borderTopStyle: "solid" }}>
        <div className="flex items-center gap-3">
          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: section.color }} />
          <div>
            <div className="text-base font-semibold tracking-tight" style={{ color: section.color }}>{section.name}</div>
            <div className="mt-0.5 text-[11px] text-zinc-500">
              {subs.length} client{subs.length === 1 ? "" : "s"} · Avg <strong className="text-zinc-700">{money(totals.avg)}/mo</strong> · Total <strong className="text-zinc-700">{money(totals.year)}/yr</strong>
            </div>
          </div>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <button data-testid={`subs-section-${section.id}-edit`} onClick={onEditSection} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-100" title="Edit section"><Pencil size={14} /></button>
            <button data-testid={`subs-section-${section.id}-add-sub`} onClick={onAddSub} className="inline-flex items-center gap-1.5 rounded-md bg-[#0066ff] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0052cc]">
              <Plus size={12} /> Add Client
            </button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="px-4 py-2 text-left font-semibold">Client</th>
              <th className="px-4 py-2 text-right font-semibold">Amount</th>
              <th className="px-4 py-2 text-left font-semibold">Frequency</th>
              <th className="px-4 py-2 text-left font-semibold">Billing</th>
              <th className="px-4 py-2 text-left font-semibold">Next Due</th>
              <th className="px-4 py-2 text-right font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {subs.map((s) => {
              const due = s.next_due_date ? parseISO(s.next_due_date) : null;
              const today = new Date(); today.setHours(0,0,0,0);
              const isDueToday = due && due.toDateString() === today.toDateString();
              const isSoon = due && (due - today) / (1000 * 60 * 60 * 24) <= 14 && due >= today && !isDueToday;
              return (
                <tr key={s.id} data-testid={`subs-row-${s.id}`} className="border-t border-zinc-100 hover:bg-zinc-50/50">
                  <td className="px-4 py-2.5 font-medium text-zinc-900">{s.client_name}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-zinc-700">{money(parseFloat(s.monthly_amount))}</td>
                  <td className="px-4 py-2.5 text-zinc-600">
                    {FREQUENCIES.find((f) => f.key === s.frequency)?.label || s.frequency}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${s.billing_mode === "automatic" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>
                      {s.billing_mode === "automatic" ? <><Zap size={10} /> Auto</> : <><AlertCircle size={10} /> Manual</>}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-700">
                    {due ? (
                      <span className={`${isDueToday ? "font-bold text-red-600" : isSoon ? "font-semibold text-amber-700" : ""}`}>
                        {format(due, "MMM d, yyyy")}
                      </span>
                    ) : <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {!readOnly && (
                      <div className="flex justify-end gap-1">
                        <button data-testid={`subs-row-${s.id}-edit`} onClick={() => onEditSub(s)} className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"><Pencil size={13} /></button>
                        <button onClick={() => removeSub(s.id)} className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={13} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {subs.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-xs text-zinc-400">No clients in this section yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent, testId }) {
  return (
    <div data-testid={testId} className="rounded-lg border border-zinc-200 bg-white p-4">
      <div className="flex items-start gap-2">
        <DollarSign size={14} className={accent} />
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
          <div className={`mt-0.5 text-xl font-semibold ${accent}`}>{value}</div>
        </div>
      </div>
    </div>
  );
}

function SectionModal({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [color, setColor] = useState(initial?.color || PALETTE[0]);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (initial) await api.put(`/subscription-sections/${initial.id}`, { name, color });
      else await api.post("/subscription-sections", { name, color });
      toast.success(initial ? "Updated" : "Section added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!initial || !window.confirm(`Delete section "${initial.name}"?`)) return;
    try {
      await api.delete(`/subscription-sections/${initial.id}`);
      toast.success("Deleted");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="section-modal" className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h2 className="text-2xl font-semibold tracking-tight text-zinc-900">{initial ? "Edit Section" : "New Section"}</h2>
          <button onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
        </div>

        <div className="mt-6 flex flex-col gap-4">
          <label className="text-sm">
            <span className="text-xs font-medium text-zinc-600">Name</span>
            <input data-testid="section-form-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20" />
          </label>
          <div>
            <div className="text-xs font-medium text-zinc-600">Color</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {PALETTE.map((c) => (
                <button
                  key={c}
                  type="button"
                  data-testid={`section-color-${c.replace('#','')}`}
                  onClick={() => setColor(c)}
                  className={`h-8 w-8 rounded-full border-2 transition-all ${color === c ? "border-zinc-900 ring-2 ring-zinc-900/20 scale-110" : "border-white"}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between gap-3">
          <div>
            {initial && <button data-testid="section-delete" onClick={remove} className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"><Trash2 size={12} /> Delete section</button>}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">Cancel</button>
            <button data-testid="section-save" onClick={save} disabled={saving} className="rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc] disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SubscriptionModal({ initial, defaultSectionId, sections, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    section_id: defaultSectionId || sections[0]?.id || "",
    client_name: "",
    monthly_amount: "",
    billing_mode: "automatic",
    frequency: "monthly",
    next_due_date: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.client_name?.trim()) { toast.error("Client name required"); return; }
    const payload = {
      ...form,
      monthly_amount: parseFloat(form.monthly_amount) || 0,
      next_due_date: form.next_due_date || null,
    };
    setSaving(true);
    try {
      if (initial) await api.put(`/subscriptions/${initial.id}`, payload);
      else await api.post("/subscriptions", payload);
      toast.success(initial ? "Updated" : "Subscription added");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="subscription-modal" className="w-full max-w-xl rounded-2xl bg-white p-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h2 className="text-2xl font-semibold tracking-tight text-zinc-900">{initial ? "Edit Subscription" : "New Subscription"}</h2>
          <button onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="text-sm md:col-span-2">
            <span className="text-xs font-medium text-zinc-600">Client Name</span>
            <input data-testid="sub-form-client" autoFocus value={form.client_name} onChange={(e) => set("client_name", e.target.value)} className={inputCls} />
          </label>
          <label className="text-sm">
            <span className="text-xs font-medium text-zinc-600">Section</span>
            <select data-testid="sub-form-section" value={form.section_id} onChange={(e) => set("section_id", e.target.value)} className={inputCls}>
              {sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="text-xs font-medium text-zinc-600">Amount per Cycle ($)</span>
            <input data-testid="sub-form-amount" type="number" step="0.01" min="0" value={form.monthly_amount} onChange={(e) => set("monthly_amount", e.target.value)} className={inputCls} />
          </label>
          <label className="text-sm">
            <span className="text-xs font-medium text-zinc-600">Frequency</span>
            <select data-testid="sub-form-frequency" value={form.frequency} onChange={(e) => set("frequency", e.target.value)} className={inputCls}>
              {FREQUENCIES.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="text-xs font-medium text-zinc-600">Billing</span>
            <select data-testid="sub-form-billing" value={form.billing_mode} onChange={(e) => set("billing_mode", e.target.value)} className={inputCls}>
              <option value="automatic">Automatic invoicing</option>
              <option value="manual">Manual invoicing</option>
            </select>
          </label>
          <label className="text-sm md:col-span-2">
            <span className="text-xs font-medium text-zinc-600">Next Due Date</span>
            <input data-testid="sub-form-due" type="date" value={form.next_due_date || ""} onChange={(e) => set("next_due_date", e.target.value)} className={inputCls} />
          </label>
          <label className="text-sm md:col-span-2">
            <span className="text-xs font-medium text-zinc-600">Notes</span>
            <textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className={inputCls} />
          </label>
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">Cancel</button>
          <button data-testid="sub-save" onClick={save} disabled={saving} className="rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc] disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20";
